/**
 * Content loading utilities
 *
 * Since Create React App doesn't support import.meta.glob,
 * we'll use a content index that gets generated at build time.
 */

export interface ContentPage {
  path: string; // URL path like "getting-started/quick-start"
  filePath: string; // Relative path to markdown file
  section: string; // Section slug like "getting-started"
  slug: string; // Page slug like "quick-start"
}

export interface ContentIndex {
  pages: ContentPage[];
  sections: {
    [section: string]: ContentPage[];
  };
}

/**
 * Load markdown content dynamically
 */
export async function loadMarkdownContent(filePath: string): Promise<string> {
  try {
    const response = await fetch(`/${filePath}`);
    if (!response.ok) {
      throw new Error(`Failed to load ${filePath}`);
    }
    return await response.text();
  } catch (error) {
    console.error(`Error loading markdown from ${filePath}:`, error);
    throw error;
  }
}

let cachedIndex: ContentIndex | null = null;

/**
 * Get content index (loaded from generated JSON)
 */
export async function getContentIndexAsync(): Promise<ContentIndex> {
  if (cachedIndex) {
    return cachedIndex;
  }

  try {
    const response = await fetch("/content/docs/contentIndex.json");
    if (!response.ok) {
      throw new Error("Failed to load content index");
    }
    const index = (await response.json()) as ContentIndex;
    cachedIndex = index;
    return index;
  } catch (error) {
    console.error("Error loading content index:", error);
    return {
      pages: [],
      sections: {},
    };
  }
}

/**
 * Get content index synchronously (uses cached version)
 */
export function getContentIndex(): ContentIndex {
  if (cachedIndex) {
    return cachedIndex;
  }
  // Return empty if not loaded yet - components should use async version
  return {
    pages: [],
    sections: {},
  };
}

/**
 * Find page by path
 */
export function findPageByPath(
  index: ContentIndex,
  section: string,
  slug?: string,
): ContentPage | null {
  if (!slug) {
    // Looking for index page
    const sectionPages = index.sections[section];
    return sectionPages?.find((p) => p.slug === "index") || null;
  }

  const sectionPages = index.sections[section];
  return sectionPages?.find((p) => p.slug === slug) || null;
}

/**
 * Get next and previous pages for navigation
 */
export function getAdjacentPages(
  index: ContentIndex,
  currentPage: ContentPage,
): { prev: ContentPage | null; next: ContentPage | null } {
  const allPages = index.pages;
  const currentIndex = allPages.findIndex((p) => p.path === currentPage.path);

  return {
    prev: currentIndex > 0 ? allPages[currentIndex - 1] : null,
    next:
      currentIndex < allPages.length - 1 ? allPages[currentIndex + 1] : null,
  };
}
